// @givmo/app-charity — tolerant decode for the intake-door responses (CBO-IN4).
//
// Same boundary and the same two rules as `enrollment-decode.ts`: every response arrives as
// `unknown` (these routes post-date the committed OpenAPI snapshot), nothing here throws, and an
// unreadable value resolves to the WIRE'S OWN declared default rather than to a neutral zero or the
// nearest familiar enum member. Read that file's header for the reasoning; this one records only what
// is different about the doors.
//
// ══ THREE THINGS THAT ARE DIFFERENT HERE ══════════════════════════════════════════════════════
//
//   1. **THERE ARE BOOLEANS ON THIS SURFACE**, unlike the lifecycle models — `tie_waived` is one.
//      They are tested `=== true`, never for truthiness: a string, a number or an object arriving on
//      a boolean is a shape this build does not understand, and reading it as "yes" guesses in the
//      direction that tells an organization a control was exercised over its books when nothing says
//      it was.
//   2. **THERE IS MONEY, AND IT ARRIVES AS INTEGER CENTS** rather than as this repo's usual decimal
//      string. The numbers-are-the-law rule survives that intact and is arguably easier to hold: an
//      integer count of cents is exact, so nothing here rounds, re-derives or accumulates a figure —
//      it is formatted for display and compared for equality, and that is all. A non-integer, a
//      numeric string, or a value outside what a JavaScript integer represents exactly reads as
//      ABSENT, because a figure this build cannot represent exactly is one it must not render.
//   3. **ABSENT AND ZERO ARE DIFFERENT ANSWERS, EVERYWHERE.** A confirmed zero is a real statement
//      about an account — it carries no opening balance — and it posts as a zero line. An absent
//      figure is nobody's statement. Any reader here that collapsed the two would turn a question
//      the organization has not answered into an answer it did not give.

import { extractList } from '@givmo/api-client/list';

/** What a person did with one proposed value. `pending` is inert — nobody has looked. */
export const INTAKE_RESOLUTIONS = ['pending', 'confirmed', 'corrected', 'declined'] as const;
export type IntakeResolution = (typeof INTAKE_RESOLUTIONS)[number];

/**
 * The resolutions an import run may proceed over.
 *
 * ⚠ DELIBERATELY NOT "ANYTHING BUT PENDING". A declined row is one somebody looked at and would not
 * answer, and a run that posted over it would post a routing nobody chose — so `declined` blocks
 * exactly as `pending` does. The two are still recorded separately, because an operator chasing a
 * stuck run needs to know which of the two it is.
 */
const ANSWERED: readonly IntakeResolution[] = ['confirmed', 'corrected'];

/** Which of the engine's map declarations a row came from. */
export const ENTITY_MAP_KINDS = ['source_account', 'merchant_account', 'bank_flow'] as const;
export type EntityMapKind = (typeof ENTITY_MAP_KINDS)[number];

/**
 * Which door a run drives.
 *
 * `opening_balances` is the charity-canonical cut-in: one balanced entry stating a position, built
 * entirely from figures this platform already holds. The other three are HISTORY rails — a
 * transaction-by-transaction carry out of a system the organization is leaving — and they read a
 * pre-taken export, which is an input this deployment does not hold.
 */
export const IMPORT_RAILS = [
  'opening_balances',
  'qbo_history',
  'card_activity',
  'bank_activity',
] as const;
export type ImportRail = (typeof IMPORT_RAILS)[number];

/**
 * The rails that actually run end to end in this build.
 *
 * ⚠ MIRRORED FROM THE BACKEND, NOT INVENTED HERE, and it is the one piece of server state this
 * module restates. The alternative was worse: the three history rails refuse BY NAME server-side with
 * a deliberately constant 503 detail ("this import is temporarily unavailable"), which is the right
 * body for a caller that could be anyone and the WRONG sentence for this console — the rails are not
 * temporarily down, their transport does not exist yet. Knowing which rails run is what lets the
 * screens say the true thing before anybody presses anything, rather than discovering it as an error
 * that misdescribes itself.
 *
 * ⚠ AND IT MUST NEVER GROW HERE FIRST. If a rail becomes executable, the backend's `EXECUTABLE_RAILS`
 * is what says so; adding one to this list ahead of that offers a control whose every use 503s.
 */
export const EXECUTABLE_RAILS: readonly ImportRail[] = ['opening_balances'];

/** `started` until it binds its record or is refused. Both terminals are recorded. */
export const IMPORT_RUN_STATES = ['started', 'bound', 'refused'] as const;
export type ImportRunState = (typeof IMPORT_RUN_STATES)[number];

/**
 * What one onboarding record is a record OF.
 *
 * ⚠ `provision` is the books being opened; `import_run` is one import that brought something in.
 * Neither is a statement that setup finished, and this build recognizes no third kind — a completion
 * artifact is the thing the module deliberately does not produce.
 */
export const ONBOARDING_RECORD_KINDS = ['provision', 'import_run'] as const;
export type OnboardingRecordKind = (typeof ONBOARDING_RECORD_KINDS)[number];

/** One source account or merchant, and where the organization says it posts. */
export interface MappingRow {
  id: string;
  mapKind: EntityMapKind | null;
  /** The key as the source system spells it — an account number, a merchant string. */
  subjectKey: string | null;
  subjectLabel: string | null;
  /** What the authored map routes it to. A PROPOSAL, never an answer. */
  proposedAccountCode: string | null;
  /** What a person stood behind. */
  resolvedAccountCode: string | null;
  resolution: IntakeResolution | null;
  /** Rendered verbatim; never paraphrased. */
  resolutionNote: string | null;
  resolvedAt: string | null;
}

/** ONE account's opening figure, answered on its own. */
export interface OpeningBalanceField {
  id: string;
  accountCode: string | null;
  /** Debit-positive cents, or `null` when no figure was proposed. `0` is a figure; `null` is not. */
  proposedAmountCents: number | null;
  recordedAmountCents: number | null;
  resolution: IntakeResolution | null;
  /** The organization's own note about where its figure came from. Rendered verbatim. */
  sourceNote: string | null;
  resolvedAt: string | null;
}

/** One attempt to bring history in. */
export interface ImportRun {
  id: string;
  rail: ImportRail | null;
  runState: ImportRunState | null;
  period: string | null;
  tieWaived: boolean;
  tieWaiverReason: string | null;
  /**
   * The refusal's machine name — an exception class, never rendered to anyone.
   *
   * ⚠ DECODED BUT NOT FOR DISPLAY. It exists so a screen can tell two refusals apart without
   * parsing prose; the sentence a person reads is `refusalDetail`, which is the refusal's own
   * message and says which subjects are still waiting or what the figures net to.
   */
  refusalType: string | null;
  refusalDetail: string | null;
  createdAt: string | null;
  boundAt: string | null;
}

/** One entry in the per-event stack. */
export interface OnboardingRecord {
  id: string;
  recordKind: OnboardingRecordKind | null;
  /** The pack these books run on, and the exact content it was pinned at. */
  rulePackId: string | null;
  rulePackContentHash: string | null;
  /** The record's own content hash — what makes it checkable rather than merely stored. */
  recordHash: string | null;
  boundAt: string | null;
  /** When the record reached its second home, or `null` while that is still outstanding. */
  storeCopiedAt: string | null;
}

/** What seeding answered: how many accounts were added, and how many a moved figure un-answered. */
export interface OpeningBalanceSeedResult {
  fieldsSeeded: number;
  fieldsReopened: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A non-empty string, or `null`. Every truthy non-string reads as absent rather than being coerced. */
function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** A member of `allowed`, or `null` for anything this build does not recognize. */
function readMember<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  if (typeof value !== 'string') return null;
  return (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/**
 * `true` only for a real `true`. Everything else — including the string `'true'` — is `false`.
 *
 * The one boolean that reaches a screen is `tie_waived`, and it labels a run as having had a control
 * deliberately stood down over it. Guessing "yes" there would tell an organization something about
 * its own books that nothing on the wire said.
 */
function readBoolean(value: unknown): boolean {
  return value === true;
}

/**
 * An exact integer count of cents, or `null`.
 *
 * As strict as `readString`, for the same reason and one more: `'1200'` is a string that happens to
 * look like a figure, and a value past `Number.MAX_SAFE_INTEGER` is a number JavaScript can hold but
 * cannot be trusted to have held EXACTLY. Both read as absent — a figure this build cannot represent
 * exactly is one it must not render beside an account name.
 *
 * ⚠ `0` IS A VALUE, not an absence, and every caller must keep the two apart.
 */
export function readCents(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return null;
  return Number.isSafeInteger(value) ? value : null;
}

/** A non-negative whole count, or `0` — the honest reading of a counter this build could not read. */
function readCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) return 0;
  return Number.isSafeInteger(value) ? value : 0;
}

/** Decode one mapping row. Returns `null` when it carries no usable id — an unaddressable row. */
export function readMappingRow(value: unknown): MappingRow | null {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  if (id === null) return null;
  return {
    id,
    mapKind: readMember(value.map_kind, ENTITY_MAP_KINDS),
    subjectKey: readString(value.subject_key),
    subjectLabel: readString(value.subject_label),
    proposedAccountCode: readString(value.proposed_account_code),
    resolvedAccountCode: readString(value.resolved_account_code),
    resolution: readMember(value.resolution, INTAKE_RESOLUTIONS),
    resolutionNote: readString(value.resolution_note),
    resolvedAt: readString(value.resolved_at),
  };
}

/** Decode one opening-balance field. Returns `null` when it carries no usable id. */
export function readOpeningBalanceField(value: unknown): OpeningBalanceField | null {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  if (id === null) return null;
  return {
    id,
    accountCode: readString(value.account_code),
    proposedAmountCents: readCents(value.proposed_amount_cents),
    recordedAmountCents: readCents(value.recorded_amount_cents),
    resolution: readMember(value.resolution, INTAKE_RESOLUTIONS),
    sourceNote: readString(value.source_note),
    resolvedAt: readString(value.resolved_at),
  };
}

/** Decode one import run. Returns `null` when it carries no usable id. */
export function readImportRun(value: unknown): ImportRun | null {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  if (id === null) return null;
  return {
    id,
    rail: readMember(value.rail, IMPORT_RAILS),
    runState: readMember(value.run_state, IMPORT_RUN_STATES),
    period: readString(value.period),
    tieWaived: readBoolean(value.tie_waived),
    tieWaiverReason: readString(value.tie_waiver_reason),
    refusalType: readString(value.refusal_type),
    refusalDetail: readString(value.refusal_detail),
    createdAt: readString(value.created_at),
    boundAt: readString(value.bound_at),
  };
}

/**
 * Decode one onboarding record. Returns `null` when it carries no usable id.
 *
 * ⚠ THE WIRE KEY IS `record_kind`, NOT `receipt_kind`. The backend renamed it deliberately: the
 * customer-facing artifact is an onboarding record, and the other noun is banned app-wide on every
 * charity surface. A reader that accepted both would let the retired name back in through the decode.
 *
 * ⚠ The HASH key beside it did NOT get renamed and is still `receipt_hash` on the wire — so this is
 * the one place in the feature where that string appears, deliberately, as a key rather than as
 * copy. It is invisible to the copy scans by construction (an underscore is a word character, so
 * there is no word boundary after the noun), which is luck rather than design: the local name is
 * `recordHash`, nothing renders the wire key, and the day the backend renames it this line is the
 * only one that moves.
 */
export function readOnboardingRecord(value: unknown): OnboardingRecord | null {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  if (id === null) return null;
  return {
    id,
    recordKind: readMember(value.record_kind, ONBOARDING_RECORD_KINDS),
    rulePackId: readString(value.rule_pack_id),
    rulePackContentHash: readString(value.rule_pack_content_hash),
    recordHash: readString(value.receipt_hash),
    boundAt: readString(value.bound_at),
    storeCopiedAt: readString(value.store_copied_at),
  };
}

/** Decode the seeding counts. An unreadable body reads as "nothing happened", never as a success. */
export function readOpeningBalanceSeedResult(value: unknown): OpeningBalanceSeedResult {
  if (!isRecord(value)) return { fieldsSeeded: 0, fieldsReopened: 0 };
  return {
    fieldsSeeded: readCount(value.fields_seeded),
    fieldsReopened: readCount(value.fields_reopened),
  };
}

/**
 * The listings.
 *
 * `extractList` reads BOTH the bare array (what the response middleware leaves after unwrapping the
 * `{data, meta, links}` envelope) and the un-unwrapped envelope, so these are correct whether or not
 * the middleware fired. Rows that decode to `null` are dropped rather than rendered as blanks.
 */
export function extractMappingRows(body: unknown): MappingRow[] {
  return extractList<unknown>(body)
    .map(readMappingRow)
    .filter((row): row is MappingRow => row !== null);
}

export function extractOpeningBalanceFields(body: unknown): OpeningBalanceField[] {
  return extractList<unknown>(body)
    .map(readOpeningBalanceField)
    .filter((field): field is OpeningBalanceField => field !== null);
}

export function extractOnboardingRecords(body: unknown): OnboardingRecord[] {
  return extractList<unknown>(body)
    .map(readOnboardingRecord)
    .filter((record): record is OnboardingRecord => record !== null);
}

/**
 * Whether one row still stands between this organization and a run.
 *
 * ⚠ THIS IS DESCRIPTIVE, NEVER A GATE. It says what the rows this client can SEE are waiting on; the
 * server sees the whole set and is the only thing that decides whether a run may proceed. Wiring a
 * control's `disabled` to this would sometimes read "ready" over a page the server would refuse — a
 * wrong answer wearing a disabled attribute — so the screens render the server's own refusal instead.
 */
export function isAwaitingAnAnswer(resolution: IntakeResolution | null): boolean {
  return resolution === null || !ANSWERED.includes(resolution);
}

/** Whether a rail actually runs end to end in this build. See `EXECUTABLE_RAILS`. */
export function isExecutableRail(rail: ImportRail | null): boolean {
  return rail !== null && EXECUTABLE_RAILS.includes(rail);
}

/**
 * Present an exact integer count of cents as an amount.
 *
 * ⚠ NO FLOATING-POINT DIVISION ANYWHERE. The cents are split by string arithmetic, so the figure
 * rendered is the figure the wire carried, digit for digit. A negative amount renders with its sign
 * rather than in parentheses: the convention on this surface is a trial balance's — debit-positive,
 * credits negative — and parentheses would be a second notation for the same fact.
 *
 * An absent figure renders as an em dash, which the callers pair with copy that says nobody has
 * stated one. `0` renders as `$0.00`, because a confirmed zero is a statement.
 */
export function displayCents(value: number | null): string {
  if (value === null) return '—';
  const negative = value < 0;
  const digits = String(Math.abs(value)).padStart(3, '0');
  const whole = digits.slice(0, -2);
  const cents = digits.slice(-2);
  let grouped = '';
  for (let index = 0; index < whole.length; index += 1) {
    if (index > 0 && (whole.length - index) % 3 === 0) grouped += ',';
    grouped += whole[index]!;
  }
  return `${negative ? '-' : ''}$${grouped}.${cents}`;
}

/**
 * Read a typed amount as exact integer cents, or `null` when it is not an amount.
 *
 * ⚠ MORE THAN TWO DECIMAL PLACES IS REFUSED, NOT ROUNDED. What a person types here becomes a line of
 * an entry that cannot be edited afterwards, so silently turning `10.005` into `$10.01` would record
 * a figure they did not state. The refusal is the honest answer; the control asks them to fix it.
 *
 * Accepts an optional leading sign, thousands separators, and either side of the point omitted
 * (`.5`, `12.`). Rejects everything else, including the empty string.
 */
export function parseCents(input: string): number | null {
  const cleaned = input.trim().replace(/,/g, '');
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (match === null) return null;
  const whole = match[2] ?? '';
  const fraction = match[3];
  if (whole.length === 0 && (fraction === undefined || fraction.length === 0)) return null;
  if (fraction !== undefined && fraction.length > 2) return null;
  const magnitude = Number(whole || '0') * 100 + Number((fraction ?? '').padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(magnitude)) return null;
  return match[1] === '-' ? -magnitude : magnitude;
}
